import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { InputDialog } from "../customComponents/InputDialog";
import UserProfileApi from "@/api-client/api/services/userProfile/userProfile";
import { CalendarForm } from "../ui/calendarPicker";
import { format } from "date-fns";


export function PopUpAddRenter({
  trigger,
  apartment_id,
  fetchTenantRelationships,
}: {
  trigger: React.ReactNode;
  apartment_id: number | null;
  fetchTenantRelationships: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [start_date, setStartDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleDialogClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const postTenantRelationship = async () => {
    if (!validateEmail(email)) {
      setEmailError(t("popUpAddRenter.invalid_email"));
      return;
    }

    try {
      const response = await UserProfileApi.postTenantRelationship({
        apartment_id: apartment_id ?? 0,
        email,
        start_date: start_date ? format(start_date, "yyyy-MM-dd") : undefined,
      });
      fetchTenantRelationships();
      handleDialogClose(false);
      return response;
    } catch (error) {
      console.error("Error while creating news:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose} modal={false}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      {isOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent
        className="max-h-full overflow-auto gap-4 w-[440px] pb-[40px]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="mb-[16px]">
          <DialogTitle className="text-[24px] font-medium flex items-center">
            {t("popUpAddRenter.adding_a_renter")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2 items-end">
            <div className="w-full">
              <InputDialog
                id="PopUpAddRenter.email"
                validationError={emailError}
                placeholder={t("popUpAddRenter.renter_email")}
                isBiggerInput={true}
                inputValue={email}
                disablePlaceholderAnimation={false}
                setValidationError={(error) => {
                  setEmailError(error ?? "");
                }}
                setInputValue={(value) => {
                  setEmail(value);
                  if (emailError && validateEmail(value)) {
                    setEmailError("");
                  }
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 items-end">
            <div className="w-full flex flex-col gap-2">
              <CalendarForm
                placeholder={t("popUpAddRenter.access_start_date")}
                setDueDate={(value) => setStartDate(new Date(value))}
                prevDueDate={start_date ? format(start_date, "yyyy-MM-dd") : ""}
                size="big"
              />
              <span className="text-[#00000080] text-[15px] font-medium">
                {t("popUpAddRenter.not_necessarily")}
              </span>
            </div>
          </div>

          <Button
            type="submit"
            variant="system"
            size="system"
            disabled={false}
            loading={false}
            onClick={postTenantRelationship}
          >
            {t("confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
